import { useState, forwardRef } from 'react';

import { useVacationRequestForm } from '@features/vacation/hooks/useVacationRequestForm';
import MobileModalHeader from '@shared/components/atoms/MobileModalHeader';
import MobileRecipientSelector from '@pages/vacation/components/mobile/MobileRecipientSelector/MobileRecipientSelector';
import styles from '@pages/vacation/components/mobile/MobileVacationRequetContent/MobileVacationRequest.module.scss';
import { formatDateToYMD } from '@shared/utils/formatDate';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { addDaysExcludeWeekend } from '@features/vacation/utils/dateUtils';

const DateButtonInput = forwardRef(function DateButtonInput(
  { value, onClick, placeholder, className, disabled },
  ref,
) {
  return (
    <button type="button" ref={ref} onClick={onClick} disabled={disabled} className={className}>
      {value || placeholder}
    </button>
  );
});

export default function MobileVacationRequest({
  onClose,
  existingVacations = [],
  onError,
  onSuccess,
}) {
  // 참조자 선택 모달 상태
  const [isRecipientSelectorOpen, setIsRecipientSelectorOpen] = useState(false);
  //휴가 신청 비즈니스로직 hook
  const {
    formData,
    setFormData,

    vacationTypes,
    ceremonyTypes,

    showVacationDivision,
    showCeremonyFields,
    showDueDate,

    handleVacationTypeChange,
    handleSave: handleHookSave,
  } = useVacationRequestForm({
    onClose,
    existingVacations,
  });

  const today = new Date();

  // 반차 여부
  const isHalfDay =
    showVacationDivision &&
    (formData.vacationDivision === 'A02' || formData.vacationDivision === 'A03');

  // 날짜 포맷팅 (yyyy-MM-dd → Date)
  function parseDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  }

  async function handleSave() {
    if (!formData.reason || formData.reason.trim() === '') {
      // 토스트만 표시
      if (typeof onError === 'function') {
        onError('신청사유를 입력해주세요.');
      }
      return; // ❗ 여기서 반드시 종료
    }
    try {
      await handleHookSave();
      // ✅ 성공 콜백 호출
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (error) {
      const message =
        error?.response?.data?.error_msg || error?.message || '휴가 신청 중 오류가 발생했습니다.';

      if (typeof onError === 'function') {
        onError(message);
      }
    }
  }

  const MAX_REASON_LENGTH = 1000;

  function handleReasonChange(e) {
    const value = e.target.value;

    // 키보드 입력 초과 차단
    if (value.length > MAX_REASON_LENGTH) {
      return;
    }
    // 자동 높이 조절
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
    setFormData((prev) => ({
      ...prev,
      reason: value,
    }));
  }

  function handleReasonPaste(e) {
    const pasteData = e.clipboardData?.getData('text') || '';
    const currentValue = formData.reason || '';

    const target = e.target;
    const selectionStart = target.selectionStart ?? 0;
    const selectionEnd = target.selectionEnd ?? 0;

    const expectedValue =
      currentValue.slice(0, selectionStart) + pasteData + currentValue.slice(selectionEnd);

    if (expectedValue.length > MAX_REASON_LENGTH) {
      e.preventDefault();
      if (typeof onError === 'function') {
        onError(`신청 사유는 최대 ${MAX_REASON_LENGTH}자까지 입력할 수 있습니다.`);
      }
    }
  }

  return (
    <div className={styles.page}>
      <MobileModalHeader title="휴가신청" onBack={onClose} />

      <div className={styles.container}>
        <div className={styles.body}>
          <div className={styles.content}>
            {/* 휴가 종류 */}
            <div className={styles.body}>
              <div className={styles.title}>휴가종류</div>
              <select
                value={formData.vacationType}
                onChange={(e) => handleVacationTypeChange(e.target.value)}
              >
                {vacationTypes.map((type) => (
                  <option key={type.code} value={type.code.toString()}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 휴가 구분 */}
            {showVacationDivision && (
              <div className={styles.body}>
                <div className={styles.title}>휴가 구분</div>
                <select
                  value={formData.vacationDivision}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      vacationDivision: e.target.value,
                    }))
                  }
                >
                  <option value="A01">종일</option>
                  <option value="A02">반차(오전)</option>
                  <option value="A03">반차(오후)</option>
                </select>
              </div>
            )}
            {/* 출산 예정일 (출산전후휴가일 경우만) */}
            {showDueDate && (
              <div className={styles.body}>
                <div className={styles.title}>출산 예정일</div>
                <div>
                  <DatePicker
                    selected={formData.dueDate || today}
                    onChange={(date) => {
                      const formatted = formatDateToYMD(date);
                      setFormData((prev) => ({ ...prev, dueDate: formatted }));
                    }}
                    locale={ko}
                    dateFormat="yyyy-MM-dd"
                    customInput={
                      <DateButtonInput className={styles.dateInput} placeholder="날짜 선택" />
                    }
                    withPortal
                    portalId="mobile-datepicker-portal"
                  />
                </div>
              </div>
            )}
            {/* 경조 구분 */}
            {showCeremonyFields && (
              <div className={styles.body}>
                <div className={styles.title}>경조 구분</div>
                <select
                  value={formData.ceremonyType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ceremonyType: e.target.value,
                      ceremonyTarget: '', // 경조 구분 변경 시 대상 초기화
                      startDate: today, // 경조 구분 변경 시 시작일 초기화
                      endDate: today, // 경조 구분 변경 시 종료일 초기화
                      period: 1, // 경조 구분 변경 시 휴가기간 초기화
                    }))
                  }
                >
                  <option value="">선택</option>
                  {ceremonyTypes.map((type) => (
                    <option key={type.type} value={type.type}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* 경조 대상 */}
            {showCeremonyFields &&
              formData.ceremonyType &&
              'A01' !== formData.ceremonyType &&
              'Z01' !== formData.ceremonyType && (
                <div className={styles.body}>
                  <div className={styles.title}>경조 대상</div>
                  <div>
                    <input
                      type="text"
                      className={`${styles.input} ${styles.ceremonyTargetBody}`}
                      value={formData.ceremonyTarget}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, ceremonyTarget: e.target.value }))
                      }
                      placeholder="경조 대상을 입력해 주세요."
                    />
                  </div>
                </div>
              )}

            {/* 휴가 일정 */}
            <div className={styles.body}>
              <div className={styles.title}>휴가일정</div>
              <div className={styles.dateRange}>
                <DatePicker
                  selected={formData.startDate || today}
                  onChange={(date) => {
                    const formatted = formatDateToYMD(date);
                    setFormData((prev) => ({
                      ...prev,
                      startDate: formatted,
                      endDate: date > parseDate(formData.startDate) ? formatted : prev.endDate,
                    }));
                  }}
                  locale={ko}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <DateButtonInput className={styles.dateInput} placeholder="시작일" />
                  }
                  withPortal
                  portalId="mobile-datepicker-portal"
                  filterDate={(date) => {
                    const day = date.getDay();
                    return day !== 0 && day !== 6; // 일요일(0), 토요일(6) 제외
                  }}
                />
                ~
                <DatePicker
                  selected={formData.endDate || today}
                  minDate={parseDate(formData.startDate)}
                  maxDate={
                    // 배우자자녀출산 휴가인 경우
                    showCeremonyFields && formData.ceremonyType && 'Z01' === formData.ceremonyType
                      ? (() => {
                          const start = parseDate(formData.startDate);
                          if (!start) return null;
                          return addDaysExcludeWeekend(start, 19);
                        })()
                      : null
                  }
                  onChange={(date) => {
                    const formatted = formatDateToYMD(date);
                    setFormData((prev) => ({ ...prev, endDate: formatted }));
                  }}
                  locale={ko}
                  dateFormat="yyyy-MM-dd"
                  customInput={
                    <DateButtonInput className={styles.dateInput} placeholder="종료일" />
                  }
                  disabled={
                    isHalfDay ||
                    (showCeremonyFields &&
                      formData.ceremonyType &&
                      formData.startDate &&
                      (() => {
                        const ceremonyTypeName =
                          ceremonyTypes.find((t) => t.type === formData.ceremonyType)?.name || '';
                        // 배우자·자녀 출산만 종료일 수정 허용
                        return !(
                          ceremonyTypeName.includes('배우자') &&
                          ceremonyTypeName.includes('자녀') &&
                          ceremonyTypeName.includes('출산')
                        );
                      })())
                  }
                  withPortal
                  portalId="mobile-datepicker-portal"
                  filterDate={(date) => {
                    const day = date.getDay();
                    return day !== 0 && day !== 6; // 일요일(0), 토요일(6) 제외
                  }}
                />
              </div>
            </div>
            {/* 배우자·자녀 출산 휴가 안내 */}
            {showCeremonyFields && formData.ceremonyType && 'Z01' === formData.ceremonyType && (
              <div className={styles.warningText}>
                배우자 출산휴가는 출산일로부터 120일 안에 총 20일을 모두 사용해야 하며, 최대 3번까지
                나누어 총 4회로 사용할 수 있음.
              </div>
            )}
            {/* 휴가 기간 */}
            <div className={styles.body}>
              <div className={styles.title}>휴가기간</div>
              <input
                type="text"
                value={formData.period}
                readOnly
                className={`${styles.period} ${styles.input}`}
              />
            </div>

            {/* 신청 사유 */}
            <div className={styles.body}>
              <div className={styles.title}>신청사유</div>
              <textarea
                value={formData.reason}
                placeholder="신청 사유를 입력하세요"
                onPaste={handleReasonPaste} //  붙여넣기 차단
                onChange={handleReasonChange} //  키보드 입력 차단
                className={styles.textArea}
              />
              {/* 글자 수 카운터 */}
              <div className={styles.charCounter}>
                <span
                  className={
                    formData.reason?.length >= MAX_REASON_LENGTH ? styles.limitReached : ''
                  }
                >
                  {formData.reason?.length || 0}
                </span>
                /{MAX_REASON_LENGTH}자
              </div>
            </div>

            <div className={styles.body}>
              <div className={styles.title}>
                참조자
                <button
                  type="button"
                  className={styles.fileSelectButton}
                  onClick={function () {
                    setIsRecipientSelectorOpen(true);
                  }}
                >
                  참조자 선택
                </button>
              </div>

              <div className={styles.referenceRow}>
                <textarea
                  className={styles.textArea}
                  value={
                    formData.recipients && formData.recipients.length > 0
                      ? formData.recipients
                          .map(function (r) {
                            return r.name;
                          })
                          .join(', ')
                      : ''
                  }
                  placeholder="선택된 참조자가 없습니다"
                  readOnly
                />
              </div>
            </div>

            {/* 참조자 선택 모달 */}
            <MobileRecipientSelector
              isOpen={isRecipientSelectorOpen}
              onClose={function () {
                setIsRecipientSelectorOpen(false);
              }}
              onSelect={function (selectedEmployees) {
                setFormData(function (prev) {
                  return {
                    ...prev,
                    recipients: selectedEmployees,
                  };
                });
              }}
              selectedRecipients={formData.recipients || []}
            />

            {/* 저장 버튼 */}
            <div className={styles.footer}>
              <button
                type="button"
                className={`${styles.actionButton} ${styles.primaryButton}`}
                onClick={handleSave}
              >
                신청하기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
``;
